import { Flex } from "@/components/Flex/Flex";
import { Grid } from "@/components/Grid/Grid";
import { Section } from "@/components/Section/Section";
import { Stack } from "@/components/Stack/Stack";
import "@/components/guide/guide-system.css";
import { SOCIAL_PROFILES } from "@/utils/blog/jsonld";

const SERVICES: Array<{ href: string; label: string }> = [
  { href: "/career-club", label: "Карьерный клуб" },
  { href: "/mentoring", label: "Менторинг и сопровождение" },
  { href: "/coaching", label: "Карьерные консультации" },
  { href: "/resume", label: "Доработка резюме" },
  { href: "/courses/dreamjob-weekend", label: "Интенсив «Как найти работу мечты»" },
  { href: "/interview", label: "Подготовка к собеседованию" },
];

const DOCS: Array<{ href: string; label: string }> = [
  { href: "https://lenacareer.school/oferta", label: "Публичная оферта" },
  { href: "https://lenacareer.school/policy", label: "Политика конфиденциальности" },
  { href: "/docs", label: "Сведения об образовательной организации" },
  { href: "https://lenacareer.school/loyalty", label: "Программа лояльности" },
];

const SOCIAL: Array<{ href: string; label: string; track: string; fill: string; path: string }> = [
  {
    href: SOCIAL_PROFILES.telegram,
    label: "Telegram",
    track: "telegram",
    fill: "#26A5E4",
    path: "M21.94 4.6l-3.32 15.66c-.25 1.1-.9 1.37-1.83.86l-5.05-3.72-2.44 2.35c-.27.27-.5.5-1.02.5l.36-5.14L17.4 6.3c.4-.36-.09-.56-.63-.2L6.4 12.7l-4.95-1.55c-1.08-.34-1.1-1.08.23-1.6l19.36-7.46c.9-.33 1.68.2 1.4 1.51z",
  },
  {
    href: SOCIAL_PROFILES.youtube,
    label: "YouTube",
    track: "youtube",
    fill: "#FF0000",
    path: "M23.5 6.5a3 3 0 0 0-2.1-2.1C19.5 3.9 12 3.9 12 3.9s-7.5 0-9.4.5A3 3 0 0 0 .5 6.5C0 8.4 0 12 0 12s0 3.6.5 5.5a3 3 0 0 0 2.1 2.1c1.9.5 9.4.5 9.4.5s7.5 0 9.4-.5a3 3 0 0 0 2.1-2.1c.5-1.9.5-5.5.5-5.5s0-3.6-.5-5.5zM9.6 15.6V8.4l6.2 3.6-6.2 3.6z",
  },
  {
    href: SOCIAL_PROFILES.dzen,
    label: "Дзен",
    track: "dzen",
    fill: "#ffffff",
    path: "M12 2c.3 5.1 1.2 7.1 3 8.9 1.8 1.8 3.8 2.7 8.9 3-.3.1-.3.1 0 .2-5.1.3-7.1 1.2-8.9 3-1.8 1.8-2.7 3.8-3 8.9-.1.1-.2.1-.2 0-.3-5.1-1.2-7.1-3-8.9-1.8-1.8-3.8-2.7-8.9-3-.1 0-.1-.2 0-.2 5.1-.3 7.1-1.2 8.9-3C10.8 9.1 11.7 7.1 12 2z",
  },
  {
    href: SOCIAL_PROFILES.vk,
    label: "VK",
    track: "vk",
    fill: "#0077FF",
    path: "M13.2 17.5c-5.4 0-8.8-3.8-8.9-10h2.7c.1 4.6 2.2 6.5 3.8 6.9V7.5h2.6v3.9c1.6-.2 3.3-2 3.9-3.9h2.5c-.4 2.3-2 4.1-3.2 4.8 1.2.6 3 2.2 3.7 5.2h-2.8c-.5-1.9-2-3.4-4.1-3.6v3.6h-1.9z",
  },
];

/**
 * Footer (`kit-footer`) — the dark brand footer shared by every premium
 * landing (the mockup `foot2` block). Content is the canonical site-wide
 * services / docs / socials / requisites set, owned here so it cannot drift
 * per page — a JSON section carries no copy (`"props": {}`). Styling lives in
 * `guide-system.css` (`.foot2`). Extracted from `GuideFooter`. JSON-composable
 * (ADR-0076 kit convention).
 */
export function KitFooter({ id }: { id?: string }) {
  return (
    <Section
      as="footer"
      id={id}
      padding="none"
      background="none"
      className="foot2"
      containerClassName="wrap"
      data-component="KitFooter"
    >
      <Grid className="foot2-grid" columns={{ base: 1, md: 2, lg: 3 }} gap={40}>
        <div className="foot2-brand foot2-col md:col-span-2 lg:col-span-1">
          <div className="f2-logo">
            Лена <i>про карьеру</i>
          </div>
          <p className="foot2-tag">
            Помогаю расти в карьере, проходить собеседования и получать предложения о работе в
            сильных компаниях.
          </p>
          <div className="foot2-sub">Подпишитесь, если ещё не успели:</div>
          <Flex className="foot2-soc" gap={12}>
            {SOCIAL.map((s) => (
              <a key={s.href} href={s.href} data-track={`guide-footer.link.${s.track}`}>
                <svg viewBox="0 0 24 24" fill={s.fill} aria-hidden="true">
                  <path d={s.path} />
                </svg>
                <span className="sr-only">{s.label}</span>
              </a>
            ))}
          </Flex>
        </div>
        <div className="foot2-col">
          <div className="foot2-srv-h">Услуги</div>
          <Stack as="nav" className="foot2-srv" gap={12}>
            {SERVICES.map((s) => (
              <a key={s.href} href={s.href}>
                {s.label}
              </a>
            ))}
          </Stack>
        </div>
        <div className="foot2-col">
          <div className="foot2-srv-h">Документы</div>
          <Stack as="nav" className="foot2-docs" gap={12}>
            {DOCS.map((d) => (
              <a key={d.href} href={d.href} target="_blank" rel="noopener noreferrer">
                {d.label}
              </a>
            ))}
          </Stack>
        </div>
      </Grid>
      <Grid
        className="foot2-bottom"
        columns={{ base: 1, lg: 3 }}
        rowGap={{ base: 16, lg: 32 }}
        columnGap={40}
        align="start"
      >
        <Stack className="foot2-contacts" gap={8}>
          <a href="https://t.me/lenacareer_care">Написать в Службу заботы →</a>
          <a href="mailto:info@lenacareer.ru">info@lenacareer.ru</a>
        </Stack>
        <div className="foot2-reqs lg:col-span-2">
          ИП Маршалова Елена Сергеевна · Дата регистрации 01.08.2024 · ИНН 100123575160 · ОГРНИП
          324774600506850 · © 2026
        </div>
      </Grid>
    </Section>
  );
}

KitFooter.displayName = "KitFooter";
